import { Preferences } from '@capacitor/preferences'

import { getPatientToken } from './auth-token'
import { isVppEdition } from './edition'
import { getManagedOrgToken } from './managed-config'
import { isNative } from './platform'
import { useEntitlementStore } from '@/stores/entitlement'

/**
 * Launch-time redemption of a facility's volume-license token.
 *
 * A clinic that bought licenses puts its token in the MDM managed app
 * configuration under `org_token`. Every device in that fleet then licenses
 * itself on first launch with nobody typing anything — which is the whole
 * point, because the alternative is reading a 16-character code aloud to a
 * room of iPads.
 *
 * Silent by construction. A wrong token, an exhausted quota or a dead network
 * must never interrupt a launch: the settings screen is where a human finds
 * out what's wrong, and the next launch retries anyway.
 */

/**
 * The last token we successfully redeemed. Purely an optimization — the
 * endpoint is idempotent for a device already on that license — but it means
 * the ordinary launch of a licensed device makes no request at all.
 *
 * Compared against the managed value, so replacing the token in the MDM
 * profile (after a rotation) re-triggers exactly once.
 */
const REDEEMED_TOKEN_KEY = 'lingotalk.orglicense.redeemed'

/**
 * Same normalization the server applies, so the marker comparison can't be
 * defeated by an admin pasting the token with different separators.
 */
function normalize(token: string): string {
  return token.toUpperCase().replace(/[^0-9A-Z]/g, '')
}

export async function syncOrgLicense(): Promise<void> {
  // Web has no managed configuration, and the VPP edition is already licensed
  // by its bundle — redeeming there would only muddy which of the two granted
  // access.
  if (!isNative || isVppEdition) return

  // The redeem endpoint authenticates as the patient, so this has to run after
  // bootstrap. Both call sites already honour that; bailing here is what makes
  // it safe to call from App.vue's mount before the token has landed.
  if ((await getPatientToken()) === null) return

  const managed = await getManagedOrgToken()
  if (managed === null) return

  const token = normalize(managed)
  if (token === '') return

  const { value: alreadyRedeemed } = await Preferences.get({ key: REDEEMED_TOKEN_KEY })
  if (alreadyRedeemed === token) return

  try {
    await useEntitlementStore().redeemOrgToken(token)
    await Preferences.set({ key: REDEEMED_TOKEN_KEY, value: token })
  } catch {
    // Invalid token, quota exhausted, offline. Leaving the marker unwritten is
    // deliberate: a quota that frees up, or an admin who fixes a typo, is
    // picked up on the next launch with no state to clear.
  }
}

/** Test-only — drops the redeemed-token marker. */
export async function _clearRedeemedMarker(): Promise<void> {
  await Preferences.remove({ key: REDEEMED_TOKEN_KEY })
}
